import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Grid, Card, CardContent, FormControl, InputLabel, Select, MenuItem, Fade,
  Paper, Divider, CircularProgress, Alert
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import StarIcon from '@mui/icons-material/Star';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import PublicIcon from '@mui/icons-material/Public';
import Loading from '../components/Loading';
import axios from 'axios';
import API_URL from '../config';

function ClientStatistics() {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsError, setStatsError] = useState(null);
    const [currentStats, setCurrentStats] = useState(null);
    const [filterCountry, setFilterCountry] = useState('');

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        const getCountries = async () => {
            try {
                const res = await axios.get(`${API_URL}/setup/countries`, { headers });
                setCountries(res.data);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }
        getCountries();
    }, []);

    useEffect(() => {
        if (!filterCountry) {
            setCurrentStats(null);
            setStatsError(null);
            return;
        }
        const getStats = async () => {
            setStatsLoading(true);
            setStatsError(null);
            try {
                const res = await axios.get(`${API_URL}/clients/client-statistics?country_id=${filterCountry}`, { headers });
                setCurrentStats(res.data);
            } catch (error) {
                console.error(error);
                setStatsError("Failed to fetch statistics. Please check CRM integration.");
                setCurrentStats(null);
            } finally {
                setStatsLoading(false);
            }
        };
        getStats();
    }, [filterCountry]);

    if (loading) return <Loading message="Loading countries..." />;

    const selectedCountryName = countries.find(c => c.id.toString() === filterCountry)?.name || '';

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            <Fade in={true} timeout={500}>
                <Box sx={{ 
                    mb: 4, 
                    mt: 2, 
                    p: 4, 
                    borderRadius: 4, 
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography variant="h4" fontWeight="800" sx={{ mb: 1, color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
                                <EqualizerIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                                Client & Quotes Statistics
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                Live statistics from Dynamics 365 Quotes &mdash; select a country to begin
                            </Typography>
                        </Box>
                        <FormControl sx={{ minWidth: 220, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                            <InputLabel sx={{ color: 'white' }}>Select Country</InputLabel>
                            <Select
                                value={filterCountry}
                                label="Select Country"
                                onChange={(e) => setFilterCountry(e.target.value)}
                                sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' }, '& .MuiSvgIcon-root': { color: 'white' } }}
                            >
                                <MenuItem value=""><em>— Choose a country —</em></MenuItem>
                                {countries.map((c) => (
                                    <MenuItem key={c.id} value={c.id.toString()}>{c.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
            </Fade>

            {/* Empty state */}
            {!filterCountry && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 340, gap: 3 }}>
                    <PublicIcon sx={{ fontSize: 90, color: '#c5cae9' }} />
                    <Typography variant="h5" fontWeight="700" color="text.secondary">
                        Select a Country to View Statistics
                    </Typography>
                    <Typography variant="body1" color="text.disabled" sx={{ maxWidth: 420, textAlign: 'center' }}>
                        Choose a country from the dropdown above. The system will retrieve all accounts and their quotes from Dynamics 365 and display the top ranked clients.
                    </Typography>
                </Box>
            )}

            {/* Loading state */}
            {filterCountry && statsLoading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 340, gap: 2 }}>
                    <CircularProgress size={60} thickness={4} sx={{ color: '#2a5298' }} />
                    <Typography variant="h6" color="text.secondary">
                        Loading statistics for <strong>{selectedCountryName}</strong>…
                    </Typography>
                    <Typography variant="body2" color="text.disabled">Syncing with Dynamics 365</Typography>
                </Box>
            )}

            {/* Error state */}
            {filterCountry && !statsLoading && statsError && (
                <Alert severity="error" sx={{ mt: 2 }}>{statsError}</Alert>
            )}

            {/* Results */}
            {filterCountry && !statsLoading && currentStats && (
                <>
                    {/* Summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', borderBottom: '4px solid #4caf50' }}>
                                <CardContent>
                                    <Typography color="text.secondary" variant="h6" gutterBottom>Quotes Won</Typography>
                                    <Typography variant="h3" fontWeight="bold" color="#4caf50" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TrendingUpIcon fontSize="large" /> {currentStats.summary.won}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', borderBottom: '4px solid #1976d2' }}>
                                <CardContent>
                                    <Typography color="text.secondary" variant="h6" gutterBottom>Amount Won</Typography>
                                    <Typography variant="h5" fontWeight="bold" color="#1976d2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <AttachMoneyIcon fontSize="medium" /> {currentStats.summary.amountWon.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', borderBottom: '4px solid #ff9800' }}>
                                <CardContent>
                                    <Typography color="text.secondary" variant="h6" gutterBottom>In Progress</Typography>
                                    <Typography variant="h3" fontWeight="bold" color="#ff9800" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AutorenewIcon fontSize="large" /> {currentStats.summary.inProgress}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', borderBottom: '4px solid #9e9e9e' }}>
                                <CardContent>
                                    <Typography color="text.secondary" variant="h6" gutterBottom>Quotes Lost</Typography>
                                    <Typography variant="h3" fontWeight="bold" color="#9e9e9e" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TrendingDownIcon fontSize="large" /> {currentStats.summary.lost}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Main Insights Grid */}
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, borderRadius: 3, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#1976d2' }}>
                                    <StarIcon /> Top Clients
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>Ranked by total quote amount</Typography>
                                <Divider sx={{ mb: 2 }} />
                                {currentStats.topRegistered.length === 0 && (
                                    <Typography color="text.disabled" sx={{ textAlign: 'center', py: 3 }}>No clients found</Typography>
                                )}
                                {currentStats.topRegistered.map((c, i) => (
                                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, p: 2, bgcolor: '#f9f9f9', borderRadius: 2 }}>
                                        <Box sx={{ flex: 1, mr: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" sx={{ bgcolor: '#1976d2', color: 'white', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 }}>{i + 1}</Typography>
                                                <Typography variant="subtitle1" fontWeight="bold">{c.name}</Typography>
                                            </Box>
                                            {c.account_number && (
                                                <Typography variant="caption" sx={{ display: 'block', ml: 3.5, mt: 0.3, color: 'info.main', fontWeight: 600 }}>{c.account_number}</Typography>
                                            )}
                                            <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5, mt: 0.3 }}>
                                                {c.type} &bull; {c.requests} quotes &bull; {c.won} won
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <Typography variant="h6" color="primary.main" fontWeight="bold">
                                                ${(c.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                            </Typography>
                                            <Typography variant="caption" color="success.main" fontWeight="bold">
                                                {c.won} won • ${(c.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, borderRadius: 3, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#ff9800' }}>
                                    <BusinessIcon /> Most Active Clients
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>Ranked by number of quotes submitted</Typography>
                                <Divider sx={{ mb: 2 }} />
                                {(!currentStats.mostActive || currentStats.mostActive.length === 0) && (
                                    <Typography color="text.disabled" sx={{ textAlign: 'center', py: 3 }}>No data found</Typography>
                                )}
                                {(currentStats.mostActive || []).map((c, i) => (
                                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 2 }}>
                                        <Box sx={{ flex: 1, mr: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" sx={{ bgcolor: '#ff9800', color: 'white', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 }}>{i + 1}</Typography>
                                                <Typography variant="subtitle1" fontWeight="bold">{c.name}</Typography>
                                            </Box>
                                            {c.account_number && (
                                                <Typography variant="caption" sx={{ display: 'block', ml: 3.5, mt: 0.3, color: '#f57c00', fontWeight: 600 }}>{c.account_number}</Typography>
                                            )}
                                            <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5, mt: 0.3 }}>{c.type}</Typography>
                                        </Box>
                                        <Typography variant="subtitle1" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                                            {c.requests} Requests
                                        </Typography>
                                    </Box>
                                ))}
                            </Paper>
                        </Grid>
                    </Grid>

                    <Grid container spacing={4} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, borderRadius: 3, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#4caf50' }}>
                                    <TrendingUpIcon /> Requested &amp; Won
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>Ranked by most won quotes</Typography>
                                <Divider sx={{ mb: 2 }} />
                                {currentStats.topRequestsAndWon.length === 0 && (
                                    <Typography color="text.disabled" sx={{ textAlign: 'center', py: 3 }}>No data available</Typography>
                                )}
                                {[...currentStats.topRequestsAndWon].sort((a, b) => b.won - a.won).map((c, i) => (
                                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 2, borderLeft: '4px solid #4caf50', bgcolor: '#f1f8e9', borderRadius: 1 }}>
                                        <Box sx={{ flex: 1, mr: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" sx={{ bgcolor: '#4caf50', color: 'white', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 }}>{i + 1}</Typography>
                                                <Typography variant="subtitle1" fontWeight="bold">{c.name}</Typography>
                                            </Box>
                                            {c.account_number && (
                                                <Typography variant="caption" sx={{ display: 'block', ml: 3.5, mt: 0.3, color: 'success.main', fontWeight: 600 }}>{c.account_number}</Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <Typography variant="body2" fontWeight="bold">{c.requests} Total</Typography>
                                            <Typography variant="caption" color="success.main" fontWeight="bold">{c.won} Won</Typography>
                                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>${(c.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, borderRadius: 3, height: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#f44336' }}>
                                    <TrendingDownIcon /> Requested but Lost
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>Ranked by most lost quotes</Typography>
                                <Divider sx={{ mb: 2 }} />
                                {currentStats.topRequestsButLost.length === 0 && (
                                    <Typography color="text.disabled" sx={{ textAlign: 'center', py: 3 }}>No data available</Typography>
                                )}
                                {[...currentStats.topRequestsButLost].sort((a, b) => b.lost - a.lost).map((c, i) => (
                                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, p: 2, borderLeft: '4px solid #f44336', bgcolor: '#ffebee', borderRadius: 1 }}>
                                        <Box sx={{ flex: 1, mr: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" sx={{ bgcolor: '#f44336', color: 'white', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', flexShrink: 0 }}>{i + 1}</Typography>
                                                <Typography variant="subtitle1" fontWeight="bold">{c.name}</Typography>
                                            </Box>
                                            {c.account_number && (
                                                <Typography variant="caption" sx={{ display: 'block', ml: 3.5, mt: 0.3, color: 'error.main', fontWeight: 600 }}>{c.account_number}</Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <Typography variant="body2" fontWeight="bold">{c.requests} Total</Typography>
                                            <Typography variant="caption" color="error.main" fontWeight="bold">{c.lost} Lost</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Paper>
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
    );
}

export default ClientStatistics;